import React, { useEffect, useState } from "react";
import { getDrRunbook, getDrTests, createDrTest, deleteDrTest } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { LifeBuoy, ChevronRight, Target, Terminal, CircleCheck, Plus, Trash2, ListChecks } from "lucide-react";
import { PrintButton } from "../components/PrintButton";

const RESULT = {
  pass: { label: "Pass", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  partial: { label: "Partial", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  fail: { label: "Fail", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export default function DR() {
  const [rb, setRb] = useState(null);
  const [tests, setTests] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ scenario: "", test_date: "", rto_target: "< 4 hours", rto_actual: "", rpo_actual: "", result: "pass", notes: "" });

  useEffect(() => {
    getDrRunbook().then(setRb).catch(() => {});
    getDrTests().then(setTests).catch(() => {});
  }, []);

  const add = async () => {
    if (!form.scenario.trim()) { toast.error("Enter a scenario"); return; }
    try {
      const c = await createDrTest(form);
      setTests((p) => [c, ...p]);
      setAdding(false);
      setForm({ scenario: "", test_date: "", rto_target: "< 4 hours", rto_actual: "", rpo_actual: "", result: "pass", notes: "" });
      toast.success("Test result logged");
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try { await deleteDrTest(id); setTests((p) => p.filter((t) => t.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (!rb) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading runbook…</div>;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> dr <ChevronRight className="h-3 w-3" /> failover runbook
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        <div className="no-print flex justify-end mb-4"><PrintButton /></div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <LifeBuoy className="h-4 w-4" /> Disaster Recovery
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">{rb.title}</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">{rb.summary}</p>

        {/* Objectives */}
        <div className="grid sm:grid-cols-3 gap-3 mt-6" data-testid="dr-objectives">
          {rb.objectives.map((o, i) => (
            <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold"><Target className="h-3.5 w-3.5" /> {o.metric}</div>
              <div className="font-heading text-2xl font-extrabold text-white mt-1">{o.target}</div>
              <div className="text-[12px] text-zinc-500 mt-1">{o.desc}</div>
            </div>
          ))}
        </div>

        {/* Phases */}
        <div className="mt-8 space-y-4">
          {rb.phases.map((p) => (
            <div key={p.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid={`dr-phase-${p.id}`}>
              <div className="font-heading font-bold text-white">{p.title}</div>
              <ul className="mt-3 space-y-1.5">
                {p.items.map((it, i) => (
                  <li key={i} className="text-[13px] text-zinc-300 flex gap-2"><span className="text-zinc-600">›</span> {it}</li>
                ))}
              </ul>
              {p.cli && (
                <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold"><Terminal className="h-3.5 w-3.5" /> CLI</div>
              )}
              {p.cli && (
                <pre className="mt-2 rounded-md border border-zinc-800 bg-black p-3 overflow-x-auto"><code className="font-mono text-[12px] leading-relaxed text-emerald-300 whitespace-pre">{p.cli.map((c) => "$ " + c).join("\n")}</code></pre>
              )}
            </div>
          ))}
        </div>

        {/* Checkpoints */}
        <div className="mt-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold mb-3"><ListChecks className="h-4 w-4" /> Recovery Checkpoints</div>
          <div className="space-y-2" data-testid="dr-checkpoints">
            {rb.checkpoints.map((c, i) => (
              <div key={i} className="flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-900 p-3">
                <CircleCheck className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[13px] text-white">{c.label}</div>
                  <div className="text-[12px] text-zinc-500 mt-0.5 font-mono">{c.how}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Test Log */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold text-white">Failover Test Log</h2>
            {!adding && (
              <button onClick={() => setAdding(true)} data-testid="add-dr-test-btn" className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium">
                <Plus className="h-3.5 w-3.5" /> Log a test
              </button>
            )}
          </div>

          {adding && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 mb-3" data-testid="add-dr-test-form">
              <div className="grid sm:grid-cols-2 gap-2">
                <Input data-testid="dr-test-scenario" value={form.scenario} onChange={(e) => setForm({ ...form, scenario: e.target.value })} placeholder="Scenario (e.g. Publisher outage)" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input data-testid="dr-test-date" value={form.test_date} onChange={(e) => setForm({ ...form, test_date: e.target.value })} placeholder="Test date (YYYY-MM-DD)" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                  <SelectTrigger data-testid="dr-test-result" className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{Object.keys(RESULT).map((r) => <SelectItem key={r} value={r}>{RESULT[r].label}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={form.rto_actual} onChange={(e) => setForm({ ...form, rto_actual: e.target.value })} placeholder="RTO achieved (e.g. 2h 40m)" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input value={form.rpo_actual} onChange={(e) => setForm({ ...form, rpo_actual: e.target.value })} placeholder="RPO achieved (e.g. 6h)" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / evidence" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setAdding(false)} className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm">Cancel</button>
                <button onClick={add} data-testid="confirm-add-dr-test" className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Save Result</button>
              </div>
            </div>
          )}

          {tests.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-zinc-600 text-sm" data-testid="dr-tests-empty">No test runs logged yet. Run the plan above and record the results.</div>
          ) : (
            <div className="space-y-2" data-testid="dr-tests-list">
              {tests.map((t) => (
                <div key={t.id} data-testid={`dr-test-${t.id}`} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white font-semibold">{t.scenario}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${RESULT[t.result]?.cls || ""}`}>{RESULT[t.result]?.label || t.result}</span>
                    </div>
                    <button onClick={() => remove(t.id)} data-testid={`delete-dr-test-${t.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[12px] text-zinc-400 font-mono">
                    {t.test_date && <span><span className="text-zinc-600">Date:</span> {t.test_date}</span>}
                    {t.rto_target && <span><span className="text-zinc-600">RTO target:</span> {t.rto_target}</span>}
                    {t.rto_actual && <span><span className="text-zinc-600">RTO actual:</span> {t.rto_actual}</span>}
                    {t.rpo_actual && <span><span className="text-zinc-600">RPO:</span> {t.rpo_actual}</span>}
                  </div>
                  {t.notes && <p className="text-[12px] text-zinc-500 mt-1.5">{t.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
